import {
  AuthenticationResult,
  PublicClientApplication,
} from '@azure/msal-browser';
import config from 'config/config';
import { isAfter } from 'date-fns';
import { getLanguage } from 'i18n/init';
import { onboardingCompleted } from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import { usePaymentStore } from 'stores/paymentStore';
import {
  cacheInFlightPromise,
  getCountryForAzureQueryParam,
} from 'utils/authUtils';
import Logger from 'utils/Logger';
import {
  acquireTokenScopes,
  loginRequestWeb,
  msalConfigSuSi,
  msalConfigSuSiTest,
} from './AuthConfig.web';

const getWebAuthReturnUrl = () => {
  const url = new URL(window.location.href);
  return `${url.protocol}//${url.host}/loginSuccess`;
};

const client = new PublicClientApplication(
  config.isE2ETest === 'true' ? msalConfigSuSiTest : msalConfigSuSi
);

client
  .handleRedirectPromise()
  .then(async (tokenResponse) => {
    AuthManager.handleTokenSuccess(tokenResponse);
  })
  .catch(() => {
    const redirectUrl = client.getConfiguration().auth.postLogoutRedirectUri;
    (window as any).open(redirectUrl, '_self');
    Logger.error('Sign in failed');
  });

class AuthManager {
  static readonly handleTokenSuccess = async (
    tokenResponse: AuthenticationResult | null
  ) => {
    if (tokenResponse) {
      const accessTokenString = tokenResponse?.accessToken || '';
      client.setActiveAccount(tokenResponse.account);
      const expires = tokenResponse.expiresOn?.getTime().toString() || null;
      useAuthStore.getState().setTokens({
        accessToken: accessTokenString,
        expireTime: expires,
        refreshToken: null,
        deviceToken: null,
        loginLoading: false,
      });
    }
  };

  static readonly suSiAsync = async () => {
    try {
      useAuthStore.getState().setLoginLoading(onboardingCompleted());
      await this.authenticate();
      /* NOTE: this never executes as it is redirected away from the workflow in web, check loginSuccess */
      return true;
    } catch (e) {
      Logger.error(`Azure sign up - ${e}`);
      return false;
    } finally {
      useAuthStore.getState().setLoginLoading(false);
    }
  };

  static readonly signOutAsync = async (_datadogLogReason?: string) => {
    // Clear storage and logout
    try {
      await client.logoutPopup();
      useAuthStore.getState().logout();
      // Local wallet-slot markers are process-global and type-only; drop them
      // on logout so a later account cannot inherit another user's "Added".
      usePaymentStore.getState().clearAllAdyenWalletSlots();
    } catch (e) {
      Logger.error(`Sign out failed - ${e}`);
    }
  };

  private static readonly authenticate = async () => {
    await client.loginRedirect({
      ...loginRequestWeb,
      redirectStartPage: getWebAuthReturnUrl(),
      extraQueryParameters: {
        ui_locales: getLanguage(),
        cnty: getCountryForAzureQueryParam(),
      },
    });
  };

  private static readonly getSilentRequest = () => {
    const silentRequest = {
      scopes: acquireTokenScopes,
      forceRefresh: false,
      account: client.getActiveAccount() || undefined,
    };
    return silentRequest;
  };

  private static readonly refreshToken = cacheInFlightPromise(() =>
    AuthManager.refreshTokenFromProvider()
  );

  private static readonly refreshTokenFromProvider = async () => {
    try {
      const tokenResponse = await client.acquireTokenSilent(
        this.getSilentRequest()
      );
      this.handleTokenSuccess(tokenResponse);
      return tokenResponse?.accessToken || '';
    } catch {
      Logger.error('Silent token refresh failed', 'AuthManager.web');
      return '';
    }
  };

  static getAccessTokenAsync = async () => {
    const accessToken = useAuthStore.getState().accessToken;
    const expireTime = useAuthStore.getState().expireTime;
    if (accessToken) {
      if (expireTime) {
        // Get expiration time - 5 minutes
        // If it's <= 5 minutes before expiration, then refresh
        const expire = new Date(parseInt(expireTime, 10));
        const now = new Date();
        if (isAfter(now, expire)) {
          try {
            return await this.refreshToken();
          } catch (e) {
            // user is not logged in anymore, TODO: notify user
            await this.signOutAsync();
            return '';
          }
        }
        return useAuthStore.getState().accessToken;
      }
      this.signOutAsync();
    }
  };
}

export default AuthManager;
